import {
  TCapTableDataItem as TCapTableDataItemFromTypes,
  TEquityInfoKS,
} from '@commenda/types/src/incorporation/types';
import { CorporationType, Country } from '@prisma/client';
import { TCapTableDataItem, TShareholderVsSharePercentageMap } from './types';

export const themeColors = [
  '#444CE7',
  '#C7D7FE',
  '#8098F9',
  '#C597BD',
  '#88BBF1',
  '#A7BF82',
  '#AA4167',
  '#AA8F21',
  '#E2AFA3',
];

function getEsopShares(
  totalShares: number,
  esopPoolPercentage: number
): number {
  return totalShares * (esopPoolPercentage / 100);
}

function getTotalEquityPercentage(
  shareholders: { equityPercentage: number }[]
): number {
  return shareholders.reduce(
    (sum, { equityPercentage }) => sum + equityPercentage,
    0
  );
}

function getEsopPoolForShareholder(
  esopShares: number,
  equityPercentage: number,
  totalEquityPercentage: number
): number {
  return (esopShares * equityPercentage) / totalEquityPercentage;
}

function getEsopPoolPercentage(esopPool: number, totalShares: number): number {
  return (esopPool / totalShares) * 100;
}

function getOwnershipPercentage(
  equityPercentage: number,
  esopPoolPercentage: number
): number {
  return equityPercentage - esopPoolPercentage;
}

function getOwnershipShares(equityShares: number, esopPool: number): number {
  return equityShares - esopPool;
}

export function calculateCapTableData(
  totalShares: number,
  shareholders: { name: string; equityPercentage: number; id: number }[] | null,
  esopPoolPercentage: number,
  sharePrice: number
): TCapTableDataItemFromTypes[] | null {
  if (!shareholders) return null;

  const esopShares = getEsopShares(totalShares, esopPoolPercentage);
  const totalEquityPercentage = getTotalEquityPercentage(shareholders);
  const ownershipPool = 100 - esopPoolPercentage;

  return shareholders.map(({ name, equityPercentage = 0, id }) => {
    const equityShares = totalShares * (equityPercentage / 100);
    const esopPool = getEsopPoolForShareholder(
      esopShares,
      equityPercentage,
      totalEquityPercentage
    );
    const esopPercentage = getEsopPoolPercentage(esopPool, totalShares);

    const ownershipPercentage = getOwnershipPercentage(
      equityPercentage,
      esopPercentage
    );

    const ownershipShares = getOwnershipShares(equityShares, esopPool);
    const amount = ownershipShares * sharePrice;

    return {
      name,
      equity: Math.max(0, parseFloat(equityPercentage.toFixed(2))),
      esopPool: Math.max(0, parseFloat(esopPercentage.toFixed(2))),
      ownership: Math.max(0, parseFloat(ownershipPercentage.toFixed(2))),
      numberOfShares: ownershipShares,
      amount,
      id,
    };
  });
}

export const getCaptableData = ({
  equityInfo,
  selectedStakeholders,
  hasESOPs,
  corporationType,
  incorporationCountry,
}: {
  equityInfo: TEquityInfoKS;
  selectedStakeholders: TShareholderVsSharePercentageMap;
  hasESOPs: boolean;
  corporationType?: CorporationType;
  incorporationCountry?: Country;
}) => {
  const isIndianPvtLtd =
    corporationType === CorporationType.PvtLtd &&
    incorporationCountry === Country.IN;
  const esopPool = hasESOPs ? equityInfo.shareRetained || 0 : 0;
  const ownershipPool = 100 - esopPool;

  let totalOwnership = 0;
  let totalActualOwnership = 0;
  let totalShares = 0;
  let totalAmount = 0;
  let totalShareCapital = 0;

  const ownershipSummaryTableRows: TCapTableDataItem[] = Object.values(
    selectedStakeholders || {}
  ).map((stakeholder) => {
    const actualOwnership = hasESOPs
      ? (ownershipPool * stakeholder.sharePercentage) / 100
      : stakeholder.sharePercentage;
    totalOwnership += stakeholder.sharePercentage || 0;
    totalActualOwnership += actualOwnership;
    const shareCount = equityInfo.shareCount || 0;
    const price = equityInfo.price || 0;
    const shareholderShares = Math.round((shareCount * actualOwnership) / 100);
    totalShares += shareholderShares;
    const shareholderAmount = Math.round(shareholderShares * price);
    totalAmount += shareholderAmount;

    const rowData: TCapTableDataItem = {
      id:
        stakeholder.type === 'INDIVIDUAL'
          ? `INDIVIDUAL_${stakeholder.id}`
          : `INSTITUTION_${stakeholder.id}`,
      name: stakeholder.name,
      ownership: stakeholder.sharePercentage
        ? stakeholder.sharePercentage.toFixed(2)
        : '',
      actualOwnership: actualOwnership ? actualOwnership.toFixed(2) : '',
      esopContribution: '',
      shares: shareholderShares.toLocaleString('en-US'),
      amount: `${shareholderAmount.toLocaleString('en-US')}`,
      shareCount: shareholderShares,
      shareValue: shareholderShares * price,
    };

    if (isIndianPvtLtd) {
      // Face value per share = perSharePrice (pricePerShare.amount)
      const faceValuePerShare = price;
      // Total Share Capital = No. of shares × perSharePrice
      const shareholderTotalShareCapital =
        shareholderShares * faceValuePerShare;
      totalShareCapital += shareholderTotalShareCapital;
      rowData.faceValue = `₹ ${faceValuePerShare}`;
      rowData.totalShareCapital = `₹ ${shareholderTotalShareCapital.toLocaleString('en-US')}`;
    }

    return rowData;
  });

  if (hasESOPs) {
    const shareCount = equityInfo.shareCount || 0;
    const price = equityInfo.price || 0;
    const esopShares = Math.round((shareCount * esopPool) / 100);
    const esopAmount = Math.round(esopShares * price);

    const esopRowData: TCapTableDataItem = {
      id: '-1',
      name: 'ESOP Shares',
      ownership: '',
      actualOwnership: '',
      esopContribution: esopPool.toFixed(2),
      shares: esopShares.toLocaleString('en-US'),
      amount: `${esopAmount.toLocaleString('en-US')}`,
      shareCount: esopShares,
      shareValue: esopShares * price,
    };

    if (isIndianPvtLtd) {
      // Face value per share = perSharePrice (pricePerShare.amount)
      const faceValuePerShare = price;
      // Total Share Capital = No. of shares × perSharePrice
      const esopTotalShareCapital = esopShares * faceValuePerShare;
      totalShareCapital += esopTotalShareCapital;
      esopRowData.faceValue = `₹ ${faceValuePerShare}`;
      esopRowData.totalShareCapital = `₹ ${esopTotalShareCapital.toLocaleString('en-US')}`;
    }

    ownershipSummaryTableRows.push(esopRowData);
  }

  const shareCount = equityInfo.shareCount || 0;
  const price = equityInfo.price || 0;
  const totalSharesCount =
    totalShares + (hasESOPs ? Math.round((shareCount * esopPool) / 100) : 0);
  const totalAmountValue = Math.round(
    ((shareCount * totalOwnership) / 100) * price
  );

  const totalRowData: TCapTableDataItem = {
    id: '-2',
    name: 'Total',
    ownership: totalOwnership.toFixed(2),
    actualOwnership: totalActualOwnership.toFixed(2),
    esopContribution: esopPool.toFixed(2),
    shares: totalSharesCount.toLocaleString('en-US'),
    amount: `${totalAmountValue.toLocaleString('en-US')}`,
    shareCount: totalSharesCount,
    shareValue: totalSharesCount * price,
  };

  if (isIndianPvtLtd) {
    // Face value per share = perSharePrice (pricePerShare.amount)
    const faceValuePerShare = price;
    // Total Share Capital = No. of shares × perSharePrice
    const totalShareCapitalValue = totalSharesCount * faceValuePerShare;
    totalRowData.faceValue = `₹ ${faceValuePerShare}`;
    totalRowData.totalShareCapital = `₹ ${totalShareCapitalValue.toLocaleString('en-US')}`;
  }

  ownershipSummaryTableRows.push(totalRowData);

  return ownershipSummaryTableRows;
};

export const formatShareValue = (value: number, currency: string) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const NON_SHAREHOLDER_ROW_IDS = new Set(['-1', '-2']);

export const getIssuedShareCount = (capTableData: TCapTableDataItem[]) =>
  capTableData
    .filter((row) => !NON_SHAREHOLDER_ROW_IDS.has(String(row.id)))
    .reduce((total, row) => total + (row.shareCount || 0), 0);
